import { Field } from "@/types/Field";
import { DataType } from "@/types/enums/DataType";
import { isPublicFormField } from "@/components/WebsiteBuilder/settings/publicFormFields";

/**
 * The public-registration field funnel, extracted out of the configurator's
 * `themes.ts` so consumers outside the builder can use it.
 *
 * `themes.ts` statically imports `lzutf8` plus all 13 `event-templates/*`
 * modules; pulling it into a light surface (the event website route) to reach
 * these two symbols would drag that whole graph into that route's chunk, and
 * `vite.config.ts` deliberately relies on `lzutf8` being dynamic-only
 * elsewhere. `themes.ts` re-exports both names, so its own consumers are
 * unaffected — this is a move, not a copy: a duplicated allowlist would drift.
 */

/**
 * DataTypes the visitor-side FormInput can actually render (FormInput.tsx
 * effectiveType branches). Anything else (GUEST_STATUS, COMPANIONS, system
 * types…) is FILTERED OUT of the pickable list rather than injected broken.
 */
export const INJECTABLE_FIELD_DATA_TYPES: ReadonlySet<DataType> = new Set([
  DataType.STRING,
  DataType.LONG,
  DataType.DOUBLE,
  DataType.CURRENCY,
  DataType.BOOLEAN,
  DataType.EMAIL,
  DataType.URL,
  DataType.DATE,
  DataType.DATE_TIME,
  DataType.TIME,
  DataType.SINGLE_SELECT,
  DataType.MULTI_SELECT,
  // Public registration image upload (Phase 2) — the visitor-side formInput
  // renders a file input driving the runtime af-image-upload endpoint. Not
  // boundable: RegistrationFieldsBody's BOUNDABLE_TYPES stays without IMAGE.
  DataType.IMAGE,
]);

/** Guest fields the registration step may offer (unsupported types out). */
export function getInjectableRegistrationFields(fields: readonly Field[]): Field[] {
  // Renderable data type AND writeable by a public submit. The catalogue also
  // carries read-only guest-list columns (`createdAt`, `guestType`, …) whose
  // presence in the form 400s every registration; this funnel feeds the
  // wizard's picker, its default selection, Velo's `injectableFieldNames` AND
  // the AI-prompt field list, so it is the one place that closes all of them.
  return fields.filter(
    (f) => INJECTABLE_FIELD_DATA_TYPES.has(f.dataType) && isPublicFormField(f),
  );
}
